import { useCallback, useEffect, useRef, useState } from 'react';

import KEYBOARD_KEYS from 'constants/keyboard';
import finchLogo from 'assets/img/finchLogo.svg';
import goBackIcon from 'assets/icons/goBackIcon.svg';
import { AppShellButton } from 'components/Button/AppShellButton';
import type { LegalDocument } from 'services/legal-documents';
import { titleForDocument, urlForDocument } from 'services/legal-documents';
import { AppLink } from 'components/Link/AppLink';
import { useLegalDocumentContent } from 'services/legal-document-content';
import { DocumentOutline } from './DocumentOutline';
import { DocumentSection } from './DocumentSection';
import styles from './LegalDocumentSheet.module.scss';

/**
 * Presents a legal document in-flow, so the version we record is the version
 * the employer saw.
 *
 * Two views deep — contents, then one section — rather than one long scroll.
 *
 * Text comes from the catalog, hash-verified server-side. If it cannot be
 * fetched the sheet offers the public page instead.
 */

type View = { kind: 'outline' } | { kind: 'section'; index: number };

export const LegalDocumentSheet = ({
  document: legalDocument,
  onClose,
  onContinue,
}: {
  document: LegalDocument;
  onClose: () => void;
  onContinue: () => void;
}) => {
  const [view, setView] = useState<View>({ kind: 'outline' });
  const [visited, setVisited] = useState<ReadonlySet<number>>(new Set());

  const bodyRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const outlineRef = useRef<HTMLDivElement>(null);
  const returningTo = useRef<number | null>(null);

  const { data: content, isPending } = useLegalDocumentContent(
    legalDocument.id,
  );
  const title = titleForDocument(legalDocument);

  // Shared by Escape and the back control so they cannot disagree.
  const goBack = useCallback(() => {
    if (view.kind === 'section') {
      returningTo.current = view.index;
      setView({ kind: 'outline' });
      return;
    }
    onClose();
  }, [view, onClose]);

  const openSection = useCallback((index: number) => {
    setVisited((seen) => new Set(seen).add(index));
    setView({ kind: 'section', index });
  }, []);

  // Each view arrives at the top, with focus on what was navigated to.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;

    if (view.kind === 'section') {
      headingRef.current?.focus();
      return;
    }

    const index = returningTo.current;
    returningTo.current = null;
    if (index === null) {
      backRef.current?.focus();
      return;
    }

    outlineRef.current
      ?.querySelector<HTMLButtonElement>(`[data-section-index="${index}"]`)
      ?.focus();
  }, [view]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === KEYBOARD_KEYS.ESCAPE) {
        event.preventDefault();
        event.stopPropagation();
        goBack();
      }

      // The sheet covers the card but does not take it out of the tab order,
      // so Tab would otherwise reach the Preamble's Continue button and Enter
      // would advance the flow from under the terms. The Preamble's Enter
      // guard only blocks its own document handler, not the button's native
      // activation — so `aria-modal` has to be backed by a real trap.
      if (event.key === KEYBOARD_KEYS.TAB) {
        const root = sheetRef.current;
        if (!root) return;

        const focusable = Array.from(
          root.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        );
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = window.document.activeElement;
        const inside = active instanceof Node && root.contains(active);

        if (!inside) {
          event.preventDefault();
          first.focus();
        } else if (event.shiftKey && active === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    // Capture phase: the Preamble binds Enter to advance, and reading the
    // terms must not skip the flow ahead.
    window.document.addEventListener('keydown', handleKeyDown, true);
    return () =>
      window.document.removeEventListener('keydown', handleKeyDown, true);
  }, [goBack]);

  // Pending and unavailable are different outcomes and must not share a view:
  // telling a reader the document failed while it is still in flight is a lie
  // that resolves itself a moment later, and sends them to the website for no
  // reason. Unavailable is real though — no stored content, or a failed hash
  // check — so say so and offer the public page rather than empty chrome.
  //
  // The refs belong on this dialog too. Without them the Tab handler finds no
  // root and returns, so focus escapes to the Preamble's Continue button
  // behind an aria-modal dialog — the same hole the trap closes on the loaded
  // view, in the state where a reader is most likely to be lost.
  if (!content) {
    return (
      <>
        <div className={styles.overlay} onClick={onClose} aria-hidden="true" />
        <div
          ref={sheetRef}
          className={styles.sheet}
          role="dialog"
          aria-modal="true"
          aria-busy={isPending}
          aria-label={title}
        >
          {/* Same three-column header as the loaded view, so the fallback does
              not reflow the chrome around it. */}
          <div className={styles.header}>
            <button
              ref={backRef}
              type="button"
              className={styles.back}
              onClick={onClose}
              aria-label={`Close ${title}`}
            >
              <img src={goBackIcon} alt="" />
            </button>
            <img className={styles.brand} src={finchLogo} alt="Finch" />
            <div aria-hidden="true" />
          </div>

          <div className={styles.body}>
            <h2 className={styles.sectionHeading}>{title}</h2>
            <p className={styles.paragraph}>
              {isPending
                ? 'Loading…'
                : 'This document could not be loaded. You can still read it on our website.'}
            </p>
            {!isPending && (
              <AppLink
                variant="plain"
                href={urlForDocument(legalDocument)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open {title}
              </AppLink>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.overlay} onClick={onClose} aria-hidden="true" />
      <div
        ref={sheetRef}
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className={styles.header}>
          <button
            ref={backRef}
            type="button"
            className={styles.back}
            onClick={goBack}
            aria-label={
              view.kind === 'section' ? 'Back to contents' : `Close ${title}`
            }
          >
            <img src={goBackIcon} alt="" />
          </button>
          <img className={styles.brand} src={finchLogo} alt="Finch" />
          <div aria-hidden="true" />
        </div>

        <div className={styles.body} ref={bodyRef}>
          {view.kind === 'outline' ? (
            <DocumentOutline
              title={title}
              effectiveDate={content.effectiveDate}
              sections={content.sections}
              visited={visited}
              onOpenSection={openSection}
              listRef={outlineRef}
            />
          ) : (
            <DocumentSection
              section={content.sections[view.index]}
              headingRef={headingRef}
            />
          )}
        </div>

        <div className={styles.footer}>
          <AppShellButton type="button" onClick={onContinue}>
            Continue
          </AppShellButton>
          <div className={styles.provenance}>
            Version {legalDocument.versionLabel}
          </div>
        </div>
      </div>
    </>
  );
};
